import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Table, Button, Container, Alert } from "react-bootstrap";
import { addDays, format } from "date-fns";
import WeekSelector from "./weekSelector";
import { useTranslation } from 'react-i18next';

const columnMapping = {
  Uber: ["Imię kierowcy", "Nazwisko kierowcy", "Wypłacono Ci", "Wypłacono Ci : Twój przychód : Opłata", "Wypłacono Ci:Twój przychód:Opłata za usługę", "Wypłacono Ci:Twój przychód:Podatki:Podatek od opłaty za usługę", "Wypłacono Ci:Twój przychód:Napiwek", "Wypłacono Ci : Bilans przejazdu : Wypłaty : Odebrana gotówka", "Wypłacono Ci:Twój przychód:Promocja:Boost+"],
  Bolt: ["Driver First Name", "Driver Last Name", "Zarobki brutto|ZŁ", "Zarobki brutto z płatności w aplikacji|ZŁ", "Zarobki brutto gotówkowe|ZŁ", "Gotówka na stanie|ZŁ", "Napiwki|ZŁ", "Koszty ogółem|ZŁ", "Zarobki netto|ZŁ", "Przewidywana wypłata|ZŁ", "Bonusy|ZŁ"],
  FreeNow: ["Booking Time", "Pickup Address", "Dropoff Address", "Fare"],
};

const columnHeaders = {
  Uber: {
    "Imię kierowcy": "driver_firstName",
    "Nazwisko kierowcy": "driver_lastName",
    "Wypłacono Ci": "incNetto",
    "Wypłacono Ci : Twój przychód : Opłata": "incBrutto",
    "Wypłacono Ci:Twój przychód:Opłata za usługę": "appFee",
    "Wypłacono Ci:Twój przychód:Podatki:Podatek od opłaty za usługę": "appFeeTaxes",
    "Wypłacono Ci:Twój przychód:Napiwek": "tips",
    "Wypłacono Ci : Bilans przejazdu : Wypłaty : Odebrana gotówka": "incCash",
    "Wypłacono Ci:Twój przychód:Promocja:Boost+": "bonus",
  },
  Bolt: {
    "Driver First Name": "driver_firstName",
    "Driver Last Name": "driver_lastName",
    "Zarobki brutto|ZŁ": "incBrutto",
    "Zarobki brutto z płatności w aplikacji|ZŁ": "incBruttoApp",
    "Zarobki brutto gotówkowe|ZŁ": "incBruttoCash",
    "Gotówka na stanie|ZŁ": "incNettoCash",
    "Napiwki|ZŁ": "tips",
    "Koszty ogółem|ZŁ": "appFee",
    "Zarobki netto|ZŁ": "incNetto",
    "Przewidywana wypłata|ZŁ": "totalInc",
    "Bonusy|ZŁ": "bonus",
  },
  FreeNow: {
    "Booking Time": "Час бронювання",
    "Pickup Address": "Адреса посадки",
    "Dropoff Address": "Адреса висадки",
    Fare: "Вартість (€)",
  },
};

const IncomingPreview = () => {
  const { t } = useTranslation();
  const { service } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [invalidFields, setInvalidFields] = useState([]);
  const [missingUsers, setMissingUsers] = useState([]);
  const [editingCell, setEditingCell] = useState({ row: null, column: null });

  useEffect(() => {
    if (!location.state?.data || location.state.data.length === 0) return;

    const allKeys = new Set();
    location.state.data.forEach((row) => {
      Object.keys(row).forEach((key) => allKeys.add(key));
    });

    let filteredKeys = columnMapping[service] || Array.from(allKeys);

    const filteredData = location.state.data.filter((row) => {
      if (service === "Bolt") {
        const payoutValue = row["Przewidywana wypłata|ZŁ"];
        return !(payoutValue === "0.00" || payoutValue === 0 || payoutValue === "" || payoutValue === undefined || payoutValue === null);
      }
      return true;
    });

    const normalizedData = filteredData.map((row) => {
      let newRow = {};
      filteredKeys.forEach((key) => {
        let newKey = columnHeaders[service]?.[key] || key;
        if (service === "Bolt" && key === "Driver First Name") {
          const fullName = row["Kierowca"] ? row["Kierowca"].trim() : "";
          newRow[newKey] = fullName.split(" ")[0] || "";
        } else if (service === "Bolt" && key === "Driver Last Name") {
          const fullName = row["Kierowca"] ? row["Kierowca"].trim() : "";
          newRow[newKey] = fullName.split(" ").slice(1).join(" ") || "";
        } else {
          newRow[newKey] = row[key] && row[key].toString().trim() !== "" ? row[key].toString() : "0";
        }
      });
      return newRow;
    });

    const unknownDrivers = [];

    const checkUsersExist = async () => {
      for (const row of normalizedData) {
        const firstName = row.driver_firstName;
        const lastName = row.driver_lastName;

        try {
          const res = await axios.get("https://godrive.com.pl/api/user-exists", {
            params: { firstName, lastName }
          });
          if (!res.data.exists) {
            unknownDrivers.push(`${firstName} ${lastName}`);
          }
        } catch (err) {
          console.error("❌ Помилка перевірки користувача:", err);
        }
      }

      setMissingUsers(unknownDrivers);
    };


    const invalids = [];
    normalizedData.forEach((row, rowIndex) => {
      Object.entries(row).forEach(([key, value]) => {
        if (getCellClass(service, key, value) === "table-danger") {
          invalids.push({
            driverFirstName: row["driver_firstName"] || "",
            driverLastName: row["driver_lastName"] || `рядок ${rowIndex + 1}`,
            columnName: key,
            value,
          });

        }
      });
    });


    setSelectedColumns(filteredKeys.map((key) => columnHeaders[service]?.[key] || key));
    setTableData(normalizedData);
    setInvalidFields(invalids);
    checkUsersExist();
  }, [location.state?.data, service]);

  const getCellClass = (service, key, value) => {
    const nameFields = [
      "driver_firstName", "driver_lastName",
      "Imię kierowcy", "Nazwisko kierowcy",
      "Driver First Name", "Driver Last Name"
    ];

    if (nameFields.includes(key) && /[^a-zA-Z]/.test(value)) {
      return "table-warning";
    }

    if (typeof value === "string" && !nameFields.includes(key)) {
      // ❌ підозріло велике ціле число (може бути дата з Excel)
      if (/^\d+$/.test(value) && Number(value) > 9999) {
        return "table-danger";
      }

      // ❌ число з комою
      if (/^-?\d+,\d+$/.test(value)) {
        return "table-danger";
      }

      // ❌ не десяткове з крапкою (але "0" дозволено)
      if (
        value !== "0" &&
        !/^-?\d+\.\d+$/.test(value)
      ) {
        return "table-danger";
      }
    }

    return "";
  };
  const recalculateInvalids = (data) => {
    const newInvalids = [];
    data.forEach((row, rowIndex) => {
      Object.entries(row).forEach(([key, value]) => {
        if (getCellClass(service, key, value) === "table-danger") {
          newInvalids.push({
            driverFirstName: row["driver_firstName"] || "",
            driverLastName: row["driver_last_name"] || `${t("line")} ${rowIndex + 1}`,
            columnName: key,
            value,
          });
        }
      });
    });
    setInvalidFields(newInvalids);
  };



  const saveToDatabase = async () => {
    if (invalidFields.length > 0) {
      alert("❌ Дані містять неправильні значення. Виправте перед збереженням.");
      return;
    }

    const updatedTableData = tableData.map(row => ({
      ...row,
      week_number: selectedWeek,
    }));

    try {
      const checkResponse = await axios.get(`https://godrive.com.pl/api/check-${service}-data?week_number=${selectedWeek}/`);
      if (checkResponse.data.exists) {
        alert(`❌ Дані за тиждень ${selectedWeek} вже є в БД!`);
        return;
      }

      const response = await axios.post(`https://godrive.com.pl/api/save-${service}-data/`, {
        data: updatedTableData,
      });

      if (response.data.success) {
        alert("✅ Дані успішно записані в БД!");
      } else {
        alert("❌ Помилка запису в БД!");
      }
    } catch (error) {
      console.error("❌ Помилка при запиті:", error);
      alert("❌ Не вдалося записати дані в БД");
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-3 text-center">{t("preview")} {service}</h2>
      <div className="mb-3">
        <label className="form-label">{t("weekNumber")}:</label>
        <WeekSelector selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} />
      </div>

      {invalidFields.length > 0 && (
        <Alert variant="danger">
          <strong>❌ {t("digitFormatErrorFounded")}:</strong>
          <ul className="mb-0">
            {invalidFields.map((err, idx) => (
              <li key={idx}>
                <strong>{err.driverFirstName} {err.driverLastName}</strong>, {t("line")} "<strong>{err.columnName}</strong>": <code>{err.value}</code>
              </li>


            ))}
          </ul>
        </Alert>
      )}

      {missingUsers.length > 0 && (
        <Alert variant="warning">
          <strong>⚠️ {t("driversNotFounded")}</strong>
          <ul className="mb-0">
            {missingUsers.map((name, idx) => {
              const [firstName, lastName] = name.split(" ");
              const createUserUrl = `/admin/addnewuser?first_name=${encodeURIComponent(firstName)}&last_name=${encodeURIComponent(lastName)}`;
              return (
                <li key={idx}>
                  {name}{" "}
                  <a
                    href={createUserUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    
                  >
                    {t("create")}?
                  </a>
                </li>
              );
            })}
          </ul>
        </Alert>
      )}



      {tableData.length === 0 ? (
        <Alert variant="warning" className="text-center">❌ {t("noRecords")}</Alert>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead className="table-dark text-center">
              <tr>
                {Object.keys(tableData[0]).map((key) => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {Object.entries(row).map(([key, value], colIndex) => (
                    <td
                      key={colIndex}
                      className={`text-center ${getCellClass(service, key, value)}`}
                      onDoubleClick={() => setEditingCell({ row: rowIndex, column: key })}
                    >
                      {editingCell.row === rowIndex && editingCell.column === key ? (
                        <input
                          type="text"
                          autoFocus
                          value={value}
                          onChange={(e) => {
                            const updated = [...tableData];
                            updated[rowIndex][key] = e.target.value;
                            setTableData(updated);
                          }}
                          onBlur={() => {
                            recalculateInvalids(tableData);
                            setEditingCell({ row: null, column: null });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              recalculateInvalids(tableData);
                              setEditingCell({ row: null, column: null });
                            }
                          }}
                        />

                      ) : (
                        value
                      )}
                    </td>

                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div className="d-flex justify-content-between mt-3">
        <Button variant="secondary" onClick={() => navigate("/admin/upload-incomings")}>⬅️ {t("back")}</Button>
        <Button variant="primary" onClick={saveToDatabase}>💾 {t("save")}</Button>
      </div>
    </Container>
  );
};

export default IncomingPreview;